'use client';

import { useMemo } from 'react';
import type { CellData, RowData } from '@tanstack/react-table';
import type { ColumnDef } from '@/lib/table-features';
import { usePermissions } from '@/hooks/use-permissions';

/**
 * Filters column definitions by per-column permission.
 *
 * A column with `meta.permission` is kept only if the current user holds
 * that permission (Super Admin always passes via {@link usePermissions}).
 * Columns with no `meta.permission` are always kept — that includes the
 * `select` and `actions` columns, which intentionally have no meta.
 *
 * Naming convention for the permission string: `{group}.column.{column_id}`
 * (e.g. `users.column.email`).
 */
export function useFilteredColumns<
  TData extends RowData,
  TValue extends CellData = CellData
>(columns: ColumnDef<TData, TValue>[]): ColumnDef<TData, TValue>[] {
  const { hasPermission } = usePermissions();

  return useMemo(
    () =>
      columns.filter((column) => {
        const permission = column.meta?.permission;
        return !permission || hasPermission(permission);
      }),
    [columns, hasPermission]
  );
}
